/**
 * Pure helpers for FromSection: resolves the FROM-side anchor for the
 * add-join popover and computes per-segment cumulative join offsets so each
 * rendered JoinLine can map its local jIdx → flat QueryStructure.joins index.
 *
 * Lives next to FromSection.tsx so the host stays under the 200-line budget.
 */

import type { QualifiedName } from '../../../../sql-grammar/model';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext } from '../../edit/editContext';

export interface JoinAnchor {
  readonly qualified: QualifiedName;
  readonly alias: string;
}

/**
 * Resolve the live QueryStructure for the FROM section so `removeTable` can run
 * its only-source detection (which is gated on `options.structure`). Returns
 * undefined on any parse/extraction failure — the caller then omits `structure`
 * and `removeTable` proceeds without the only-source confirm gate.
 */
export function resolveFromStructure(
  editContext: VqeEditContext,
): QueryStructure | undefined {
  try {
    return extractStructureForContext(editContext);
  } catch {
    return undefined;
  }
}

export function resolveJoinAnchor(editContext: VqeEditContext): JoinAnchor | undefined {
  try {
    const structure = extractStructureForContext(editContext);
    const first = structure?.fromItems?.[0];
    if (!first) return undefined;
    if (first.target.kind !== 'relation') return undefined;
    const qualified = first.target.qualified;
    const alias = first.alias?.alias ?? qualified.name;
    return { qualified, alias };
  } catch {
    return undefined;
  }
}

interface SegmentLike {
  readonly split: { readonly joins: readonly { readonly kind: unknown }[] };
}

export function computeJoinOffsets(segments: readonly SegmentLike[]): number[] {
  const offsets: number[] = [];
  let running = 0;
  for (const seg of segments) {
    offsets.push(running);
    running += seg.split.joins.length;
  }
  return offsets;
}
